import React, { useEffect } from 'react';
import { QueryClient, QueryClientProvider } from 'react-query';
import {
  BrowserRouter,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';

import NewSessionPage from '@components/pages/Sessions/NewSessionPage';
import { AppContextProvider, Env } from '@contexts/AppContext';
import { useScrollToTop } from '@hooks';
import useCurrentUser from '@hooks/useCurrentUser';
import { STRIPE_PUBLISH_KEY } from '@src/env';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js/pure';
import { User } from '@utils/backend-api/currentUser';
import { EMAIL_MESSAGE_CONFIRMATION } from '@utils/constants';

import EmailConfirmation from '../EmailConfirmation';
import CommunityInsights from '../pages/CommunityInsights';
import EditForumPostPage from '../pages/Forum/EditForumPostPage';
import ForumIndexPage from '../pages/Forum/ForumIndexPage';
import ForumLayout from '../pages/Forum/ForumLayout';
import ForumPostPage from '../pages/Forum/ForumPostPage';
import Organization from '../pages/Organization';
import OrganizationLayout from '../pages/Organization/Layout';
import PaymentStatus from '../pages/PaymentStatus';
import SettingsPage from '../pages/Settings/SettingsPage';
import Account from '../pages/Settings/SettingsPage/Account';
import Affiliate from '../pages/Settings/SettingsPage/Affiliate';
import EmailPreferences from '../pages/Settings/SettingsPage/EmailPreferences';
import Gift from '../pages/Settings/SettingsPage/Gift';
import Team from '../pages/Settings/SettingsPage/Team';
import EditTopicsSettings from '../pages/Settings/UserProfile/EditTopicsSettings';
import SubscriberDirectory from '../pages/SubscriberDirectory';
import { SubscriberProfileSPA } from '../pages/SubscriberProfile';
import UpdatePayment from '../pages/UpdatePayment';
import MetaTagLayout from './MetaTagLayout';
import ProtectedRoute from './ProtectedRoute';

const queryClient = new QueryClient();
const stripePromise = loadStripe(STRIPE_PUBLISH_KEY);

function AppRoutes() {
  useScrollToTop();
  const location = useLocation();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useCurrentUser();
  const firstName = currentUser?.name.split(' ')[0];

  // Set the rc param on every location change
  useEffect(() => {
    if (currentUser?.affiliate_code && !params.get('rc')) {
      try {
        params.set('rc', currentUser.affiliate_code);
        navigate(`${location.pathname}?${params.toString()}${location.hash}`, {
          replace: true,
        });
      } catch (e) {
        // eslint-disable-next-line no-empty
        // Nothing to handle here, but we should catch so that failing to overwrite query params doesn't break the page, which has historically happened on unsubscribe links routed via sendgrid
      }
    }
  }, [params, currentUser?.affiliate_code, navigate, location.pathname, location.hash]);

  return (
    <Routes>
      <Route element={<MetaTagLayout />}>
        <Route path="/sessions/new" element={<NewSessionPage />}></Route>
        <Route element={<ForumLayout />}>
          <Route path="/forum" element={<ForumIndexPage />} />
          <Route path="/forum/recent" element={<ForumIndexPage order="most_recent" />} />
          <Route path="/forum/comments" element={<ForumIndexPage order="most_comments" />} />
          <Route path="/forum/posts/:id" element={<ForumPostPage />} />
          <Route path="/forum/posts/:id/edit" element={<EditForumPostPage />} />
          <Route path="/forum/posts/new" element={<EditForumPostPage />} />
        </Route>
        <Route path="/organization" element={<OrganizationLayout firstName={firstName} />}>
          <Route path="account" element={<Organization />} />
        </Route>
        <Route path="/insights" element={<CommunityInsights />} />
        <Route path="/directory" element={<SubscriberDirectory />} />
        <Route path="/u/:username" element={<SubscriberProfileSPA />} />
        <Route path="/u/:username/:article_page_id" element={<SubscriberProfileSPA />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/payment-status" element={<PaymentStatus />} />
          <Route path="/subscription/update-payment" element={<UpdatePayment />} />
          <Route element={<SettingsPage />}>
            <Route path="/unsubscribe" element={<EmailPreferences />} />
          </Route>
          <Route path="/settings" element={<SettingsPage />}>
            <Route index element={<Account />} />
            <Route path="email-preferences" element={<EmailPreferences />} />
            <Route path="affiliate-settings" element={<Affiliate />} />
            <Route path="gift-subscription" element={<Gift />} />
            <Route path="team-settings" element={<Team />} />
            <Route path="topics" element={<EditTopicsSettings />} />
          </Route>
        </Route>
        <Route
          path="/email-confirmation"
          element={<EmailConfirmation message={EMAIL_MESSAGE_CONFIRMATION} />}
        />
      </Route>
    </Routes>
  );
}

const App = ({
  env,
  initialCurrentUser,
  isWebview,
}: {
  env: Env;
  initialCurrentUser: User;
  isWebview: boolean;
}) => {
  return (
    <AppContextProvider env={env} initialCurrentUser={initialCurrentUser} isWebview={isWebview}>
      <QueryClientProvider client={queryClient}>
        <Elements stripe={stripePromise}>
          <div className="App">
            <BrowserRouter>
              <AppRoutes />
            </BrowserRouter>
          </div>
        </Elements>
      </QueryClientProvider>
    </AppContextProvider>
  );
};

export default App;
